import React from "react";
import {
  Box,
  Typography,
  Chip,
  Divider,
} from "@mui/material";
import { AlertCircle, Phone, MapPin, NotebookText } from "lucide-react";
import { getTransportIcon } from "../../../utils/transportIcons";

/* ── Helpers ────────────────────────────────────────────────────────────── */

function fmtDate(val) {
  if (!val) return "–";
  try {
    const d = val?.toDate ? val.toDate() : new Date(val);
    return d.toLocaleDateString("fi-FI", {
      day: "numeric",
      month: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "–";
  }
}

function getSenderName(delivery, members) {
  if (delivery.senderInfo?.name) return delivery.senderInfo.name;
  if (delivery.senderId && members?.length > 0) {
    const member = members.find((m) => m.userId === delivery.senderId);
    if (member?.name) return member.name;
  }
  return "–";
}

function getLatenessWarning(delivery) {
  const now = Date.now();
  const pickup = delivery.pickupTime ?? delivery.pickUpTime;
  const target = delivery.targetTime;

  if (delivery.status === "active" && pickup && pickup < now) {
    const mins = Math.round((now - pickup) / 60000);
    return `Nouto myöhässä — piti noutaa ${fmtDate(pickup)}, nyt ${mins} min myöhässä`;
  }
  if (delivery.status === "picked" && target && target < now) {
    const mins = Math.round((now - target) / 60000);
    return `Toimitus myöhässä — määräaika ${fmtDate(target)}, nyt ${mins} min myöhässä`;
  }
  return null;
}

/* ── Sub-components ─────────────────────────────────────────────────────── */

function PhoneLink({ phone }) {
  if (!phone) {
    return (
      <Typography variant="caption" color="text.secondary">
        Ei numeroa
      </Typography>
    );
  }
  return (
    <Box
      component="a"
      href={`tel:${phone}`}
      onClick={(e) => e.stopPropagation()}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        fontSize: "0.7rem",
        color: "info.main",
        textDecoration: "none",
        bgcolor: "action.selected",
        px: 1,
        py: 0.25,
        borderRadius: 10,
        border: "0.5px solid",
        borderColor: "info.light",
        "&:hover": { bgcolor: "action.hover" },
      }}
    >
      <Phone size={10} />
      {phone}
    </Box>
  );
}

function PersonCard({ label, name, phone }) {
  return (
    <Box
      sx={{
        bgcolor: "background.paper",
        border: "0.5px solid",
        borderColor: "divider",
        borderRadius: 1,
        p: 1.25,
        minWidth: 140,
        flex: "1 1 140px",
      }}
    >
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{
          fontSize: "0.65rem",
          textTransform: "uppercase",
          letterSpacing: "0.04em",
          display: "block",
          mb: 0.5,
        }}
      >
        {label}
      </Typography>
      <Typography
        variant="caption"
        fontWeight={600}
        sx={{ display: "block", mb: 0.75 }}
        noWrap
      >
        {name || "–"}
      </Typography>
      <PhoneLink phone={phone} />
    </Box>
  );
}

function AddressBlock({ icon: Icon, label, address, time, timeLabel, instructions }) {
  const hasInstructions = !!instructions;
  return (
    <Box
      sx={{
        bgcolor: "background.paper",
        border: "0.5px solid",
        borderColor: "divider",
        borderRadius: 1,
        p: 1.25,
        flex: 1,
        minWidth: 0,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.75 }}>
        <Icon size={11} opacity={0.5} />
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontSize: "0.65rem", textTransform: "uppercase", letterSpacing: "0.04em" }}
        >
          {label}
        </Typography>
      </Box>
      <Typography variant="caption" fontWeight={600} sx={{ display: "block" }}>
        {address || "–"}
      </Typography>
      {time && (
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.68rem", display: "block", mt: 0.25 }}>
          {timeLabel} {fmtDate(time)}
        </Typography>
      )}
      {hasInstructions && (
        <Box
          sx={{
            mt: 0.75,
            pt: 0.75,
            borderTop: "0.5px solid",
            borderColor: "divider",
            display: "flex",
            gap: 0.5,
            alignItems: "flex-start",
          }}
        >
          <NotebookText size={10} opacity={0.45} style={{ marginTop: 2, flexShrink: 0 }} />
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.68rem", lineHeight: 1.4 }}>
            {instructions}
          </Typography>
        </Box>
      )}
    </Box>
  );
}

function SpecPill({ label, warning = false }) {
  if (!label) return null;
  return (
    <Chip
      label={label}
      size="small"
      variant="outlined"
      color={warning ? "warning" : "default"}
      sx={{ fontSize: "0.65rem", height: 20 }}
    />
  );
}

function SectionLabel({ children }) {
  return (
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ fontSize: "0.65rem", textTransform: "uppercase", letterSpacing: "0.04em", fontWeight: 700 }}
    >
      {children}
    </Typography>
  );
}

/* ── Main export ────────────────────────────────────────────────────────── */

export function ExpandedDeliveryRow({ delivery, members = [] }) {
  const warning = getLatenessWarning(delivery);
  const distanceKm = delivery.distance
    ? `${(delivery.distance).toFixed(1)} km`
    : null;

  const flagPills = [
    delivery.temperatureControl && { label: "lämpötilahallinta", warning: true  },
    delivery.canBeLeftAtDoor    && { label: "voi jättää ovelle",  warning: false },
    delivery.returnParcels      && { label: "palautuslähetys",    warning: false },
  ].filter(Boolean);

  const people = [
    {
      label: "Lähettäjä",
      name:  getSenderName(delivery, members),
      phone: delivery.senderPhone,
    },
    {
      label: "Kuljettaja",
      name:  delivery.courierName,
      phone: delivery.courierPhone,
    },
    {
      label: "Vastaanottaja",
      name:  delivery.receiverInfo?.name,
      phone: delivery.receiverPhone,
    },
  ];

  return (
    <Box
      sx={{
        px: 2,
        py: 1.5,
        mx: 0.5,
        mb: 1,
        bgcolor: "background.default",
        border: "0.5px solid",
        borderColor: "divider",
        borderRadius: 1.5,
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
      }}
    >
      {/* ── Late warning ── */}
      {warning && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            bgcolor: "warning.50",
            border: "0.5px solid",
            borderColor: "warning.main",
            borderRadius: 1,
            px: 1.5,
            py: 0.75,
          }}
        >
          <AlertCircle size={13} />
          <Typography variant="caption" sx={{ fontWeight: 500, color: "warning.dark" }}>
            {warning}
          </Typography>
        </Box>
      )}

      {/* ── Addresses + instructions ── */}
      <Box>
        <SectionLabel>Reitti</SectionLabel>
        <Box sx={{ display: "flex", gap: 1, mt: 0.75, flexWrap: "wrap" }}>
          <AddressBlock
            icon={MapPin}
            label="Nouto"
            address={delivery.originInfo?.address || delivery.origin}
            time={delivery.pickupTime ?? delivery.pickUpTime}
            timeLabel="Noutoaika:"
            instructions={delivery.originInfo?.details?.instructions}
          />
          <AddressBlock
            icon={MapPin}
            label="Toimitus"
            address={delivery.destinationInfo?.address || delivery.destination}
            time={delivery.targetTime}
            timeLabel="Kohdeaika:"
            instructions={delivery.destinationInfo?.details?.instructions}
          />
        </Box>
      </Box>

      <Divider sx={{ borderColor: "divider", opacity: 0.6 }} />

      {/* ── People ── */}
      <Box>
        <SectionLabel>Henkilöt</SectionLabel>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 0.75 }}>
          {people.map((p) => (
            <PersonCard key={p.label} {...p} />
          ))}
        </Box>
      </Box>

      <Divider sx={{ borderColor: "divider", opacity: 0.6 }} />

      {/* ── Spec pills ── */}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, alignItems: "center" }}>
        {getTransportIcon(delivery.deliveryTransportType, 14)}
        <SpecPill label={delivery.deliveryTransportType} />
        <SpecPill label={delivery.packageSize} />
        {delivery.weightKg    && <SpecPill label={`${delivery.weightKg} kg`} />}
        {distanceKm           && <SpecPill label={distanceKm} />}
        {delivery.price != null && (
          <SpecPill
            label={`Hinta ${delivery.price.toFixed(2)} € / Palkkio ${delivery.reward?.toFixed(2) ?? "–"} €`}
          />
        )}
        {flagPills.map((p) => (
          <SpecPill key={p.label} {...p} />
        ))}
      </Box>
    </Box>
  );
}